/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from '@mui/material'
import { METRIC_ENDPOINT_RANK, METRIC_ENDPOINT_WEIGHT, RANKED_COLOR } from 'constant'
import { useAppSelector } from 'hooks'
import { useMemo } from 'react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import {
  calculateMetricGroupForTable,
  calculateWeightedGlobalBarChart,
  generateGlobalBarChartData,
  isAllMetricValueEmpty,
  isAllPillarValueEmpty,
} from 'utils'
import { CustomizedDot } from './utils'

export interface SlopeChartReportProps {
  name: string | undefined
}

const SlopeChartReport = ({ name }: SlopeChartReportProps) => {
  const configuration = useAppSelector(({ vibrancy }) => vibrancy.configuration)
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const metricGroups = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  const metricGroup = calculateMetricGroupForTable(metricGroups, { metricValue: configuration.metricValue })
  const renderChartTimes = useAppSelector(({ vibrancy }) => vibrancy.renderChartTimes)

  // const renderChartData = calculateWeightedGlobalBarChart(
  //   metricGroups,
  //   configuration.metricValue,
  //   countryData,
  //   configuration.yearFilter.currentYear,
  // )
  // const slopeData = useMemo<Country[]>(() => {
  //   return calculateCountryDataFromMetricValue(metricGroup, renderChartData, {
  //     globalYear: configuration.yearFilter.currentYear,
  //     metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
  //     metricEndpointRank: METRIC_ENDPOINT_RANK,
  //     metricValue: configuration.metricValue,
  //   })
  // }, [renderChartData.length, metricGroup.length, renderChartTimes])

  // const data = useMemo<SlopeData>(() => {
  //   if (!name) return []
  //   return generateSlopeChartData(metricGroup, slopeData, {
  //     yearRange: configuration.yearFilter.yearRange,
  //     metricEndpointRank: METRIC_ENDPOINT_RANK,
  //     metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
  //     metricValue: configuration.metricValue,
  //     sort: 'asc',
  //   }).map((el) => ({
  //     year: el.year,
  //     // 'United States': el['United States'],
  //     [name]: el[name],
  //   }))
  // }, [slopeData.length, metricGroup.length, renderChartTimes, name])

  const data = useMemo(() => {
    if (metricGroups.length === 0 || countryData.length === 0 || configuration.yearFilter.yearRange.length === 0) {
      return []
    }
    let list: any = []
    try {
      list = [...configuration?.yearFilter?.yearRange]
        ?.sort((a, b) => a - b)
        ?.map((year) => {
          const renderChartDataBar = calculateWeightedGlobalBarChart(
            metricGroups,
            configuration.metricValue,
            countryData,
            year,
          )
          const dataReturn = generateGlobalBarChartData(metricGroups, renderChartDataBar, {
            globalYear: year,
            metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
            metricEndpointRank: METRIC_ENDPOINT_RANK,
            metricValue: configuration.metricValue,
          })
          const rankObject: Record<string, string | number> = {
            year: year?.toString(),
          }
          dataReturn?.forEach((el, index) => {
            rankObject[el.countryName] = index + 1
          })
          return rankObject
        })
        .map((el) => ({
          year: el.year,
          // 'United States': el['United States'],
          [name]: el[name],
        }))
    } catch (error) {}
    return list
  }, [countryData, metricGroups, renderChartTimes, configuration.yearFilter.yearRange, renderChartTimes, name])

  const countryTicks = useMemo<{ countryName: string; rank: number; color: string }[]>(() => {
    if (!data || data.length === 0) return []
    return Object.entries(data[0])
      .filter(([key]) => key !== 'year')
      .map(([key, value]) => ({ countryName: key, rank: value as number }))
      .map((el, index) => ({ ...el, color: RANKED_COLOR[index % 5] }))
  }, [data])
  return (
    <Box
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
    >
      <span style={{ fontFamily: 'CircularStdMedium' }}>Ranking Over Time</span>
      <Box
        sx={{
          height: configuration.yearFilter.yearRange.length * 60,
          width: '100%',
          mr: '60px',
          mt: '4px',
        }}
      >
        {!isAllPillarValueEmpty(metricGroups) && !isAllMetricValueEmpty(metricGroups, configuration.metricValue) && (
          <ResponsiveContainer height='100%'>
            <LineChart
              data={data}
              margin={{
                top: 40,
                right: 30,
                left: 20,
                bottom: 25,
              }}
            >
              <defs>
                <filter id={`lineShadow`} x='-50%' y='-50%' width='200%' height='200%' filterUnits='userSpaceOnUse'>
                  <feOffset result='offOut' in='SourceGraphic' dx='5' dy='5' />
                  <feColorMatrix
                    result='matrixOut'
                    in='offOut'
                    type='matrix'
                    values='0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0.5 0'
                  />
                  <feGaussianBlur result='blurOut' in='matrixOut' stdDeviation='3' />
                  <feBlend in='SourceGraphic' in2='blurOut' mode='normal' />
                </filter>
              </defs>
              <XAxis
                tick={{ dy: 20, fill: 'black' }}
                type='category'
                dataKey='year'
                axisLine={false}
                tickLine={false}
                allowDuplicatedCategory={false}
                interval='preserveStartEnd'
                style={{ fontSize: '11px' }}
              />
              <YAxis
                type='number'
                reversed={true}
                domain={[1, 'dataMax']}
                axisLine={false}
                tickLine={false}
                tick={false}
              />
              <CartesianGrid horizontal={false} />
              {countryTicks.map((countryTick) => {
                return (
                  <Line
                    key={countryTick.countryName}
                    dataKey={countryTick.countryName}
                    stroke={countryTick.color}
                    dot={<CustomizedDot value={countryTick.rank} boxWidth={40} boxHeight={40} />}
                    isAnimationActive={false}
                  />
                )
              })}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  )
}

export default SlopeChartReport
